import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import {
  Input,
  Button,
  Select,
  Form,
  Progress,
  Tooltip,
  Divider,
  Spin,
  Popconfirm,
  Radio,
  Checkbox,
  Card,
  message
} from 'antd'
import 'antd/dist/antd.css'
import { config } from '../../../../../../../../../constants'
import './AiImages.scss'
import { ImagePreview } from '../../../../../../../../../components'
import { api } from '../../../../../../../../../services'
import sendMessageIcon from '../../images/send.png'
import dummyUserImg from '../../../../../../../../../assets/user.svg'
import { useSelector } from 'react-redux'
import ImageCarousel from './ImageCarousel'
import ImageTextForm from './ImageTextForm'
import AspectRatioSelector from './AspectRatioSelector'
import { useTranslation } from 'react-i18next'
import { T } from 'antd/lib/upload/utils'

const { Option } = Select
const AiImages = ({
  form,
  onImage,
  selectedImage,
  isImageBuilder,
  isUploading,
  isBackground = false
}) => {
  const { getFieldDecorator, setFieldsValue } = form

  const [promptText, setPromptText] = useState('')
  const [promptTitle, setPromptTitle] = useState('')
  const [includeText, setIncludeText] = useState(false)
  const [generationStatus, setGenerationStatus] = useState({
    status: 'idle',
    percentage: 0,
    isVariation: false
  })
  const [generatedImageArray, setGeneratedImageArray] = useState([])
  const [selectedPosition, setSelectedPosition] = useState(1)
  const [upscaledImage, setUpscaledImage] = useState('')
  const [currentGeneratedImage, setCurrentGeneratedImage] = useState({
    info: '',
    url: ''
  })
  const endOfPagesRef = useRef(null)

  let ws

  const { t } = useTranslation()
  useEffect(() => {
    const cleanup = () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close()
        console.log('WebSocket closed.')
      }
    }

    return cleanup
  }, [ws])

  useEffect(() => {
    if (
      generationStatus.status === 'waiting-to-start' ||
      generationStatus.status === 'running' ||
      generationStatus.status === 'complete'
    ) {
      goToEndOfPages()
    }
  }, [generationStatus.status])

  const goToEndOfPages = () => {
    if (endOfPagesRef.current) {
      endOfPagesRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }

  const handleGenerateImage = (prompt = '') => {
    if (prompt) {
      startGeneration(prompt)
    } else {
      form.validateFields(async (errors, values) => {
        if (errors || !promptText) {
          return
        }

        startGeneration()
      })
    }
  }

  const startGeneration = value => {
    let prompt = value || promptText
    if (promptTitle) {
      prompt = `${prompt} with text "${promptTitle}"`
    }
    if (!isImageBuilder) {
      generateWithIdeoGram(value || promptText)
      return
    }

    // Get selected aspect ratio if it exists
    form.validateFields(async (errors, values) => {
      if (errors) return

      // Add aspect ratio to prompt if selected
      if (values.aspectRatio && values.aspectRatio !== 'default') {
        prompt = `${prompt} --ar ${values.aspectRatio}`
      }

      setGenerationStatus({ status: 'waiting-to-start', percentage: 0 })
      setCurrentGeneratedImage({ info: '', url: '' })

      api.imageGeneration
        .generateImage({
          prompt: prompt
        })
        .then(response => {
          setupWebsocket(response.data.MessageId)
        })
        .catch(error => {
          console.error(error)
          setGenerationStatus({ status: 'error', percentage: 0 })
        })
    })
  }

  const generateWithIdeoGram = (prompt, negativePrompt = '') => {
    form.validateFields(async (errors, values) => {
      if (errors) return
      setGeneratedImageArray([])
      let fullPrompt = `Prompt: "${prompt ||
        values.prompt}". Aspect ratio: ASPECT_16_9. `
      let fullNegativePrompt = negativePrompt || values.negativePrompt || ''

      if (isBackground) {
        fullPrompt = `${t(
          'v5:create_a_subtle_and_non_distracting_background_image'
        )} "${prompt || values.prompt}". ${t(
          'v5:the_image_should_incorporate_elements_of'
        )} ${prompt || values.prompt} ${t(
          'v5:vary_abstract_faded_or_minimalist_way'
        )}`

        // Add default negative prompts for backgrounds
        fullNegativePrompt +=
          ', text, words, letters, numbers, strong contrasts, vibrant colors, distinct shapes, logos, icons, realistic depictions, detailed illustrations, foreground elements'
      }

      if (includeText && !isBackground) {
        fullPrompt += `Text visible in image: "${values.title}". `
        fullPrompt += `Text position must be: "${values.textPosition}". `
        fullPrompt += `The text must be easily visible but not dominate the image. `
        fullPrompt += `Font size: ${values.fontSize}. `
        fullPrompt += `Font type: ${values.fontType}. `
        fullPrompt += `Font style: ${values.fontStyle}. `
        fullPrompt += `Text color: ${values.textColor}. `
      }

      const payload = {
        prompt: fullPrompt,
        negative_prompt: fullNegativePrompt,
        aspect_ratio: 'ASPECT_16_9',
        model: 'V_2',
        magic_prompt_option: 'ON'
      }
      setGenerationStatus({ status: 'running', percentage: '0' })

      handleGeneration(payload, values)
    })
  }

  const handleGeneration = async (payload, values) => {
    const { numImages } = values
    let completedImages = 0

    const updateProgress = () => {
      completedImages++
      const percentage = (completedImages / numImages) * 100
      setGenerationStatus({
        status:
          parseInt(completedImages) === parseInt(numImages)
            ? 'complete'
            : 'running',
        percentage: percentage.toFixed(0)
      })
    }

    try {
      for (let i = 0; i < numImages; i++) {
        const response = await api.imageGeneration.generateIdeogramImage(
          payload
        )

        if (!response || !response.data || !response.data.data) {
          throw new Error('No image data received')
        }

        const data = response.data.data
        if (data && data.length > 0 && data[0]) {
          const imageObject = {
            link: data[0].url,
            prompt: data[0].prompt,
            resolution: data[0].resolution,
            isImageSafe: data[0].is_image_safe,
            seed: data[0].seed,
            title: values.text,
            textPosition: values.textPosition,
            aspectRatio: values.aspectRatio
          }
          setGeneratedImageArray(prevState => [...prevState, imageObject])

          updateProgress()
        } else {
          throw new Error('No image data received')
        }
      }
    } catch (error) {
      console.error('Error:', error)
      setGenerationStatus({
        status: 'error',
        percentage: 0
      })
      message.error('Failed to generate image. Please try again.')
    }
  }

  const setupWebsocket = (messageId, isVariation = false) => {
    const socketUrl =
      config.midJourneySocket +
      (isVariation
        ? `?channel_type=SEND_VARIATION&channel_id=SEND-VARIATION-MESSAGEID-${messageId}`
        : `?channel_type=SEND_IMAGINE&channel_id=SEND-IMAGINE-MESSAGEID-${messageId}`)

    ws = new WebSocket(socketUrl)

    ws.onopen = () => {
      console.log('Websocket connected')
    }

    ws.onclose = () => {
      console.log('Websocket closed')
    }

    ws.onmessage = event => {
      const data = JSON.parse(event.data)
      const isGenerating =
        data.event === 'sendImagine' || data.event === 'sendVariation'
      if (isGenerating && data.progress === 'done') {
        setGenerationStatus({
          status: 'complete',
          percentage: 100,
          isVariation: data.event === 'sendVariation'
        })
        setCurrentGeneratedImage({ info: data.msg, url: data.msg.uri })
        ws.close()
      } else if (isGenerating) {
        setGenerationStatus({
          status: 'running',
          percentage: parseInt(data.progress.replace(/\D/g, ''), 10),
          isVariation: data.event === 'sendVariation'
        })
        setCurrentGeneratedImage({ info: '', url: data.url })
      }
    }
  }

  const generateVariations = () => {
    setGenerationStatus({
      status: 'waiting-to-start',
      percentage: 0,
      isVariation: true
    })

    api.imageGeneration
      .generateVariation({
        index: selectedPosition,
        msgId: currentGeneratedImage.info.id,
        hash: currentGeneratedImage.info.hash
      })
      .then(response => {
        setupWebsocket(response.data.MessageId, true)
      })
      .catch(error => {
        console.error(error)
        setGenerationStatus({ status: 'error', percentage: 0 })
      })
  }

  const upscaleImage = () => {
    setGenerationStatus({ status: 'upscalingImage', percentage: 0 })
    const data = {
      msgId: currentGeneratedImage.info.id,
      hash: currentGeneratedImage.info.hash,
      index: selectedPosition
    }

    api.imageGeneration
      .upscaleImage(data)
      .then(response => {
        const upscaledImageURL = response.data.uri
        setUpscaledImage(upscaledImageURL)
        setGenerationStatus({ status: 'complete', percentage: 100 })
      })
      .catch(error => {
        console.error(error)
        setGenerationStatus({ status: 'error', percentage: 0 })
      })
  }

  const handleOperationClick = operation => {
    if (operation === 'generate') {
      generateVariations()
    } else if (operation === 'upscale') {
      upscaleImage()
    }
  }

  const selectImage = (imageObj = {}) => {
    if (
      isImageBuilder &&
      selectedImage &&
      selectedImage.link === upscaledImage
    ) {
      return
    }

    onImage({
      link: isImageBuilder ? upscaledImage : imageObj.link,
      id: generate7DigitRandomInteger()
    })
  }

  function generate7DigitRandomInteger() {
    const min = 1000000 // Minimum value (inclusive)
    const max = 9999999 // Maximum value (inclusive)
    return Math.floor(Math.random() * (max - min + 1)) + min
  }

  const isImageBeingGenerated = useMemo(() => {
    return (
      generationStatus.status !== 'idle' &&
      generationStatus.status !== 'error' &&
      generationStatus.status !== 'complete'
    )
  }, [generationStatus])
  const handleGenerateFromAi = prompt => {
    setPromptText(prompt)
    setFieldsValue({ prompt: prompt })
    //scroll to div with id of promptInput

    const promptInput = document.getElementById('promptInput')
    if (promptInput) {
      promptInput.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <div className="ai-images-container">
      <div className="ai-images-user-input">
        <AiImagesForm
          form={form}
          getFieldDecorator={getFieldDecorator}
          promptText={promptText}
          isImageBeingGenerated={isImageBeingGenerated}
          handleGenerateImage={handleGenerateImage}
          setPromptText={setPromptText}
          promptTitle={promptTitle}
          setPromptTitle={setPromptTitle}
          generationStatus={generationStatus}
          isImageBuilder={isImageBuilder}
          includeText={includeText}
          setIncludeText={setIncludeText}
          isBackground={isBackground}
        />
        <AiChat
          generateImage={handleGenerateFromAi}
          isImageBeingGenerated={isImageBeingGenerated}
        />
      </div>

      {generationStatus.status !== 'idle' && (
        <div className="ai-images-result">
          {currentGeneratedImage.url && (
            <div className="image-preview">
              <h3>{promptText}</h3>
              <ImagePreview
                src={currentGeneratedImage.url}
                alt="Generated Image"
              />
            </div>
          )}

          {generationStatus.status === 'complete' && isImageBuilder && (
            <div className="image-preview-actions">
              <Form.Item
                label={t('v5:select_position_of_image_you_like')}
                name="position"
              >
                <Select
                  value={selectedPosition}
                  onChange={value => setSelectedPosition(value)}
                >
                  <Option value={1}>{t('v5:top_left')}</Option>
                  <Option value={2}>{t('v5:top_right')}</Option>
                  <Option value={3}>{t('v5:bottom_left')}</Option>
                  <Option value={4}>{t('v5:bottom_right')}</Option>
                </Select>
              </Form.Item>
              {currentGeneratedImage.info && (
                <div className="image-preview-actions-buttons">
                  <ButtonWithTooltip
                    type="primary"
                    showTooltip={!selectedPosition}
                    tooltip={t('v5:select_a_position_of_image')}
                    onButtonClick={() => handleOperationClick('generate')}
                    buttonText={t('v5:generate_variations')}
                  />
                  <ButtonWithTooltip
                    type="primary"
                    showTooltip={!selectedPosition}
                    tooltip={t('v5:select_a_position_of_image_upscale')}
                    onButtonClick={() => handleOperationClick('upscale')}
                    buttonText={t('v5:upscale_image_for_selection')}
                  />
                </div>
              )}
            </div>
          )}

          {generationStatus.status === 'running' && (
            <div className="image-generation-status">
              <Progress percent={parseFloat(generationStatus.percentage)} />
              <p>
                {generationStatus.isVariation
                  ? t('v5:generation_variations')
                  : t('v5:generating_your_image')}
              </p>
            </div>
          )}

          {generationStatus.status === 'waiting-to-start' && (
            <Spin
              // indicator={antIcon}
              tip={`${t('v5:waiting_start_generation')} ${
                generationStatus.isVariation
                  ? t('v5:variations')
                  : t('media:image')
              }.`}
            />
          )}

          {generationStatus.status === 'upscalingImage' && (
            <Spin tip={t('v5:upscaling_image')} />
          )}

          {generationStatus.status === 'error' && (
            <p>{t('v5:error_generating')}.</p>
          )}
          {upscaledImage && !isImageBeingGenerated && isImageBuilder && (
            <>
              <div className="image-preview">
                <h2>
                  {isImageBuilder
                    ? t('v5:upscaled_image')
                    : t('v5:ai_generate_image')}
                </h2>
                <ImagePreview src={upscaledImage} alt="Upscaled Image" />
              </div>

              <Button
                type={
                  selectedImage && selectedImage.link === upscaledImage
                    ? 'default'
                    : 'primary'
                }
                onClick={selectImage}
                loading={isUploading}
              >
                {isUploading
                  ? t('general:uploading')
                  : selectedImage && selectedImage.link === upscaledImage
                  ? t('v5:selected')
                  : t('v5:select')}
              </Button>
            </>
          )}

          {!isImageBeingGenerated && !isImageBuilder && (
            <ImageCarousel
              images={generatedImageArray}
              onSelectImage={selectImage}
            />
          )}
        </div>
      )}
      <div ref={endOfPagesRef} />
    </div>
  )
}

const ButtonWithTooltip = ({
  tooltip,
  onButtonClick,
  buttonText,
  showTooltip,
  ...props
}) => {
  if (showTooltip) {
    return (
      <Tooltip title={tooltip}>
        <Button disabled {...props}>
          {buttonText}
        </Button>
      </Tooltip>
    )
  }
  return (
    <Button onClick={onButtonClick} {...props}>
      {buttonText}
    </Button>
  )
}

const AiImagesForm = ({
  form,
  getFieldDecorator,
  promptText,
  isImageBeingGenerated,
  handleGenerateImage,
  setPromptText,
  promptTitle,
  setPromptTitle,
  generationStatus,
  isImageBuilder,
  includeText,
  setIncludeText,
  isBackground
}) => {
  const showIncludeText = isBackground ? false : true
  const { t } = useTranslation()
  return (
    <Form
      className="ai-images-form"
      onFinish={() => handleGenerateImage()}
      layout="vertical"
    >
      <div className="ai-images-form-prompt" id="promptInput">
        <h1>⚡ {t('v4:ai_image_generation')}</h1>
        <Divider style={{ color: '#000' }}>
          {t('v5:generate_image_using_your_image')}
        </Divider>
      </div>
      <Form.Item name="prompt">
        {getFieldDecorator('prompt', {
          initialValue: promptText,
          rules: [
            {
              required: true,
              message: t('v5:please_enter_your_idea')
            }
          ]
        })(
          <Input.TextArea
            disabled={isImageBeingGenerated}
            placeholder={t('v5:your_prompt_here')}
            value={promptText}
            autoSize={{ minRows: 2, maxRows: 5 }}
            onChange={e => setPromptText(e.target.value)}
          />
        )}
      </Form.Item>{' '}
      {/* Add aspect ratio selector for Midjourney */}
      {isImageBuilder && (
        <AspectRatioSelector form={form} disabled={isImageBeingGenerated} />
      )}
      {showIncludeText && (
        <Form.Item required={false} colon={false} className="custom-form-item">
          {getFieldDecorator('includeText', {
            valuePropName: 'checked',
            initialValue: includeText
          })(
            <Checkbox
              disabled={isImageBeingGenerated}
              onChange={e => setIncludeText(e.target.checked)}
              className="include-text"
            >
              {t('v5:add_text_in_the_image')}
            </Checkbox>
          )}
        </Form.Item>
      )}
      {includeText && (
        <Form.Item name="title" label={t('v5:icon_text')}>
          {getFieldDecorator('title', {
            initialValue: promptTitle,
            rules: [
              {
                required: !isImageBuilder,
                message: t('v4:please_enter_text_to_appear_image')
              }
            ]
          })(
            <Input
              disabled={isImageBeingGenerated}
              value={promptTitle}
              size="large"
              placeholder={t('v4:enter_text_to_apear_in_the_image')}
              onChange={e => setPromptTitle(e.target.value)}
            />
          )}
        </Form.Item>
      )}
      {!isImageBuilder && (
        <ImageTextForm
          form={form}
          includeText={includeText}
          isImageBeingGenerated={isImageBeingGenerated}
        />
      )}
      <Form.Item>
        <Button
          onClick={() => handleGenerateImage()}
          disabled={
            isImageBuilder
              ? isImageBeingGenerated && generationStatus.status !== 'error'
              : false
          }
          type="primary"
          size="large"
          loading={isImageBeingGenerated}
        >
          {t('v4:generate_image')}
        </Button>
      </Form.Item>
    </Form>
  )
}

const AiChat = ({ generateImage, isImageBeingGenerated }) => {
  const { t } = useTranslation()
  const defaultMessages = [
    {
      user: false,
      message: t('v5:what_type_image_do_you_generate')
    }
  ]

  const [isChatStarted, setIsChatStarted] = useState(false)
  const [userInput, setUserInput] = useState('')
  const [messages, setMessages] = useState(defaultMessages)
  const [isReceivingMessage, setIsReceivingMessage] = useState(false)
  const [context, setContext] = useState([])
  const [revisedPrompt, setRevisedPrompt] = useState('')

  const messagesEndRef = useRef(null)

  const handleSubmit = () => {
    if (!userInput) return
    const data = {
      params: {
        prompt: userInput,
        context: context
      }
    }
    setMessages([...messages, { user: true, message: userInput }])
    setUserInput('')
    setIsReceivingMessage(true)
    api.imageGeneration.conversationAI(data).then(response => {
      const body = response.data.body
      setMessages(prevMsgs => [
        ...prevMsgs,
        { user: false, message: body && body[0].response }
      ])
      setContext(body && body[0].context)
      setRevisedPrompt(body && body[0].revised_prompt)
      setIsReceivingMessage(false)
    })
  }

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }

  useEffect(() => {
    if (userInput && !isReceivingMessage) return
    scrollToBottom()
  }, [messages, userInput, isReceivingMessage])

  const handleStartNewChat = () => {
    setIsChatStarted(true)
    setMessages(defaultMessages)
    setContext([])
    setRevisedPrompt('')
  }

  const user = useSelector(state => state.user)

  const userImg =
    (user && user.info && user.info.avatar && user.info.avatar.link) ||
    dummyUserImg
  const renderChat = () => {
    return (
      <div className="ai-midjourney-chat">
        <div className="ai-midjourney-chat-messages">
          {messages.map((message, index) => (
            <div
              className={`ai-midjourney-chat-message ${
                message.user ? 'ai-midjourney-chat-message--user' : ''
              }`}
              key={index}
            >
              <div className="ai-midjourney-chat-message-avatar">
                {message.user ? <img src={userImg} alt="user" /> : 'AI'}
              </div>
              <p>{message.message}</p>
            </div>
          ))}

          {revisedPrompt && !isReceivingMessage && (
            <div className="ai-midjourney-chat-message ai-midjourney-chat-message--revised">
              <Button
                onClick={handleChatButton}
                disabled={isImageBeingGenerated}
              >
                {t('v4:generate_image_using_suggested_prompt')}
              </Button>
              <Popconfirm
                title={t('v5:are_you_sure_you_want_to_start_a_new_chat')}
                okText={t('v4:yes')}
                cancelText={t('v4:no')}
                icon={null}
                onConfirm={handleStartNewChat}
              >
                <div className="ai-midjourney-chat-start-new">
                  {t('v5:start_new_chat')}
                </div>
              </Popconfirm>
            </div>
          )}

          {isReceivingMessage && (
            <div className={`ai-midjourney-chat-message`}>
              <div className="ai-midjourney-chat-message-avatar">AI</div>
              <div className="dot-container">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <Form
          className="ai-midjourney-chat-input"
          onFinish={() => handleSubmit()}
        >
          <Input
            placeholder={t('v5:type_message')}
            className="ai-midjourney-chat-input"
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            onPressEnter={() => handleSubmit()}
            disabled={isReceivingMessage || isImageBeingGenerated}
            suffix={
              <Tooltip title={t('v3:send')}>
                <img
                  className="ai-midjourney-chat-input-send"
                  onClick={() => {
                    if (isReceivingMessage || isImageBeingGenerated) return
                    handleSubmit()
                  }}
                  src={sendMessageIcon}
                  style={{ opacity: userInput ? 1 : 0.3 }}
                  alt="send message"
                />
              </Tooltip>
            }
          />
        </Form>
      </div>
    )
  }

  const handleChatButton = () => {
    generateImage(revisedPrompt)

    setIsChatStarted(!isChatStarted)
  }

  return (
    <div className="ai-midjourney-chat-container">
      <p>{t('v4:talk_to_ai_about_the_image_generate')}</p>

      {isChatStarted && renderChat()}

      <Button
        disabled={isImageBeingGenerated}
        onClick={() => {
          setIsChatStarted(!isChatStarted)
          if (!isChatStarted) {
            handleStartNewChat()
          }
        }}
        type="primary"
        ghost
      >
        {isChatStarted ? t('v5:stop_chat') : t('v5:start_chat')}
      </Button>
    </div>
  )
}

export default Form.create()(AiImages)
